/**
 * Service buttons in place of a service grid. The grid is no longer offered;
 * a new kiosk design starts with buttons, and an old grid is swapped for them.
 */

import { menuFor, SERVICE_GAP } from '../lib/kioskMenu'
import type { Service } from '../lib/types'
import { newButton, onMenuPage } from './pages'
import { pageIds, type LayoutDocument, type LayoutElement } from './types'

/** A button's tallest and shortest row before another column is added. */
const ROW_MAX = 200
const ROW_MIN = 110

type Box = { x: number; y: number; w: number; h: number }

/** Where n buttons go in a box: rows from the top in the given columns, with
 *  another column only when the rows would get shorter than ROW_MIN. */
export function buttonSlots(n: number, box: Box, columns = 2): Box[] {
  if (n <= 0) return []
  const gap = SERVICE_GAP
  const rowsFit = Math.max(1, Math.floor((box.h + gap) / (ROW_MIN + gap)))
  const cols = Math.max(1, columns, Math.ceil(n / rowsFit))
  const rows = Math.ceil(n / cols)
  const h = Math.max(1, Math.min(ROW_MAX, Math.floor((box.h - (rows - 1) * gap) / rows)))
  const w = Math.max(1, Math.floor((box.w - (cols - 1) * gap) / cols))
  return Array.from({ length: n }, (_, i) => ({
    x: box.x + (i % cols) * (w + gap),
    y: box.y + Math.floor(i / cols) * (h + gap),
    w,
    h,
  }))
}

/**
 * The design with one grid swapped for a service button per service it showed:
 * its chosen services in their order, else every offered one. The buttons are
 * the editor's own (newButton), on the grid's pages, in the grid's box.
 */
export function replaceGrid(doc: LayoutDocument, gridId: string, offered: readonly Service[]): LayoutDocument {
  const grid = doc.elements.find((el) => el.id === gridId && el.kind === 'services')
  if (!grid) return doc
  const services = menuFor(
    offered.filter((s) => s.active),
    grid.options?.serviceIds,
  )
  const known = pageIds(doc)
  const own = (grid.pages ?? []).filter((p) => known.includes(p))
  const pages = own.length > 0 ? own : known
  let next: LayoutDocument = { ...doc, elements: doc.elements.filter((el) => el.id !== gridId) }
  for (const page of pages) {
    // A service that already has its button on this page keeps that one.
    const has = new Set(
      next.elements.flatMap((el) => (el.kind === 'button' && el.action?.kind === 'service' && onMenuPage(el, page) ? [el.action.serviceId] : [])),
    )
    const missing = services.filter((s) => !has.has(s.id))
    const slots = buttonSlots(missing.length, grid, grid.options?.columns ?? 2)
    missing.forEach((s, i) => {
      const button: LayoutElement = { ...newButton(next, page, { kind: 'service', serviceId: s.id }), ...slots[i]! }
      next = { ...next, elements: [...next.elements, button] }
    })
  }
  return next
}

/** Every grid in the design swapped for buttons: how a new kiosk design starts. */
export function withoutGrids(doc: LayoutDocument, offered: readonly Service[]): LayoutDocument {
  if (doc.kind !== 'kiosk') return doc
  return doc.elements.filter((el) => el.kind === 'services').reduce((d, grid) => replaceGrid(d, grid.id, offered), doc)
}
